'use client'

import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { Testimonial } from '@/types/coolxprint2'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

const DEFAULT_TESTIMONIAL_IDS = ['1', '2', '3']

interface TestimonialsSectionProps {
  testimonials?: Testimonial[]
}

export function TestimonialsSection({
  testimonials,
}: TestimonialsSectionProps) {
  const t = useTranslations('cxp.home.testimonials')

  // Filter active testimonials and sort by order
  const activeTestimonials =
    testimonials
      ?.filter((testimonial) => testimonial.is_active)
      .sort((a, b) => a.order - b.order) || []

  const displayTestimonials: Testimonial[] =
    activeTestimonials.length > 0
      ? activeTestimonials
      : DEFAULT_TESTIMONIAL_IDS.map((id, index) => ({
          id,
          name: t(`item_${id}.name`),
          company: t(`item_${id}.company`),
          quote: t(`item_${id}.quote`),
          avatar: '/image/2026/12/29/mceclip1.png',
          company_logo: '/image/2026/12/29/mceclip0_60.png',
          image: '/image/2026/12/29/mceclip0.png',
          order: index,
          is_active: true,
        }))

  const [api, setApi] = useState<CarouselApi | null>(null)

  return (
    <div className=''>
      {/* Testimonials Carousel - Split Layout */}
      {displayTestimonials.length > 0 && (
        <Carousel
          setApi={setApi}
          opts={{
            align: 'start',
            loop: true,
          }}
          className='w-full'
        >
          <CarouselContent>
            {displayTestimonials.map((testimonial) => (
              <CarouselItem
                key={testimonial.id}
                className='basis-full px-6 py-8 md:px-10 md:py-12 lg:px-16 lg:py-16'
              >
                <div className='grid grid-cols-1 gap-4 md:gap-10 lg:grid-cols-2'>
                  {/* Left Section - Testimonial */}
                  <div className='relative flex flex-col'>
                    {/* Avatar and Name - Top Left */}
                    <div className='mb-3 flex items-center gap-4 md:mb-8'>
                      <div className='relative h-16 w-16 overflow-hidden rounded-full bg-[#ff4e00] sm:h-20 sm:w-20 md:h-24 md:w-24'>
                        {testimonial.avatar ? (
                          <Image
                            src={ImageHelper.getImageUrl(testimonial.avatar)}
                            alt={testimonial.name}
                            fill
                            className='object-cover'
                            sizes='(max-width: 640px) 64px, (max-width: 768px) 80px, 96px'
                          />
                        ) : (
                          <div className='h-full w-full bg-neutral-300' />
                        )}
                      </div>
                      <p className='font-sans text-lg font-semibold text-neutral-900 max-md:max-w-[150px] md:text-xl'>
                        {testimonial.name}
                      </p>
                    </div>

                    {/* Company Logo - Top Right */}
                    {testimonial.company_logo && (
                      <div className='absolute right-6 top-0.5 md:right-10 md:top-10 lg:right-16 lg:top-16'>
                        <div className='relative h-12 w-24 sm:h-14 sm:w-32 md:h-16 md:w-36 lg:h-20 lg:w-44'>
                          <Image
                            src={ImageHelper.getImageUrl(
                              testimonial.company_logo
                            )}
                            alt={testimonial.company}
                            fill
                            className='object-contain'
                            sizes='(max-width: 640px) 96px, (max-width: 768px) 128px, 176px'
                          />
                        </div>
                      </div>
                    )}

                    {/* Large Quote Icon */}
                    <div className='mb-6 max-md:hidden md:mb-8'>
                      <svg
                        className='h-16 w-20 text-neutral-300 md:h-20 md:w-24 lg:h-24 lg:w-28'
                        fill='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path d='M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.996 2.151c-2.433.917-3.995 3.638-3.995 5.849h4v10h-9.984zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.99 3.638-3.99 5.849h3.983v10h-9.989z' />
                      </svg>
                    </div>

                    {/* Quote Text */}
                    <div className='mb-4 flex-1 md:mb-10'>
                      <p className='font-sans text-sm leading-relaxed text-neutral-800 md:text-base lg:text-lg'>
                        {testimonial.quote}
                      </p>
                    </div>

                    {/* Navigation Arrows - Bottom Left */}
                    <div className='flex items-center gap-3 max-md:mb-4'>
                      <button
                        onClick={() => api?.scrollPrev()}
                        className='flex h-10 w-10 items-center justify-center rounded-full bg-neutral-200 text-neutral-600 transition-colors hover:bg-neutral-300 md:h-12 md:w-12'
                        aria-label='Previous testimonial'
                      >
                        <ChevronLeft className='h-5 w-5 md:h-6 md:w-6' />
                      </button>
                      <button
                        onClick={() => api?.scrollNext()}
                        className='flex h-10 w-10 items-center justify-center rounded-full bg-neutral-900 text-white transition-colors hover:bg-neutral-800 md:h-12 md:w-12'
                        aria-label='Next testimonial'
                      >
                        <ChevronRight className='h-5 w-5 md:h-6 md:w-6' />
                      </button>
                    </div>
                  </div>

                  {/* Right Section - Image */}
                  <div className='relative h-[300px] w-full rounded-2xl md:h-[400px] lg:h-full lg:min-h-[500px]'>
                    {testimonial.review_image ? (
                      <Image
                        src={ImageHelper.getImageUrl(testimonial.review_image)}
                        alt={`${testimonial.name} - ${testimonial.company}`}
                        fill
                        className='rounded-2xl object-cover'
                        sizes='(max-width: 1024px) 100vw, 50vw'
                      />
                    ) : (
                      <div className='h-full w-full rounded-2xl bg-neutral-300' />
                    )}
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}

      {/* CTA Button */}
      <div className='flex justify-center mb-4 md:mb-10'>
        <Link href='#contact-form'>
          <Button size='large' variant='black'>
            {t('cta_button')}
          </Button>
        </Link>
      </div>
    </div>
  )
}
